import { r as registerTranslation, t as translation, a as translation$1, _ as __decorate, b as __metadata, F as FormControl, c as FormControlController } from '../en-czXvriqi.js';
import { css, nothing, html } from 'lit';
import { unsafeStatic, html as html$1 } from 'lit/static-html.js';
import { query, property, state, customElement } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { f as formatSize } from '../utils-xlxkOHqp.js';
import 'lit/directive.js';

/**
 * Dispatches a cancelable custom event and runs the provided default action
 * only if the consumer did not call `event.preventDefault()`.
 *
 * This is the shared convention used across components (Tabs, Modal, Chip, Alert,
 * Forward, Back) to provide a sensible default JavaScript behavior out of the box
 * while letting consumers intercept and override it. Notification deliberately does
 * NOT use this: its show/close are always caller-initiated (no default markup-driven
 * trigger to override), so there was no real use case for blocking them — see
 * it-notification.ts for the plain (non-cancelable) events it fires instead.
 *
 * ```js
 * el.addEventListener('it-alert-close', (e) => {
 *   e.preventDefault();                    // block the default removal
 *   fetchConfirmation().then((ok) => {     // async logic runs after prevention
 *     if (ok) e.target.close();
 *   });
 * });
 * ```
 *
 * Note: `preventDefault()` must be called synchronously inside the listener.
 * Async code can run afterward; call the component's public method when ready.
 *
 * @param host the element dispatching the event
 * @param name the event name (kebab-case, `it-` prefixed)
 * @param detail the event `detail` payload
 * @param defaultAction the behavior executed when the event is not prevented
 * @returns the dispatched event (inspect `defaultPrevented` if needed)
 */
function dispatchCancelable(host, name, detail, defaultAction) {
    const event = new CustomEvent(name, {
        bubbles: true,
        composed: true,
        cancelable: true,
        detail,
    });
    host.dispatchEvent(event);
    if (!event.defaultPrevented) {
        defaultAction();
    }
    return event;
}

var styles = css`@charset "UTF-8";
*,
*::before,
*::after {
  box-sizing: border-box;
}

@media (prefers-reduced-motion: no-preference) {
  :root {
    scroll-behavior: smooth;
  }
}

body {
  margin: 0;
  -webkit-text-size-adjust: 100%;
  -webkit-tap-highlight-color: hsla(0, 0%, 0%, 0);
}

hr {
  margin: 1rem 0;
  color: inherit;
  border: 0;
  border-top: 1px solid;
  opacity: 0.25;
}

p {
  margin-top: 0;
  margin-bottom: var(--bsi-paragraph-spacing);
}

abbr[title] {
  text-decoration: underline dotted;
  cursor: help;
  text-decoration-skip-ink: none;
}

address {
  margin-bottom: var(--bsi-spacing-s);
  font-style: normal;
  line-height: inherit;
}

ol,
ul {
  padding-left: var(--bsi-spacing-l);
}

ol,
ul,
dl {
  margin-top: 0;
  margin-bottom: var(--bsi-spacing-s);
}

ol ol,
ul ul,
ol ul,
ul ol {
  margin-bottom: 0;
}

dt {
  font-weight: var(--bsi-font-weight-strong);
}

dd {
  margin-bottom: var(--bsi-spacing-xxs);
  margin-left: 0;
}

blockquote {
  margin: 0 0 var(--bsi-spacing-s);
}

sub,
sup {
  position: relative;
  font-size: var(--bsi-font-size-1);
  line-height: 0;
  vertical-align: baseline;
}

sub {
  bottom: -0.25em;
}

sup {
  top: -0.5em;
}

a {
  color: var(--bsi-color-link);
  text-decoration: underline;
  text-decoration-skip-ink: auto;
  text-underline-offset: 2px;
}
a:hover {
  color: var(--bsi-color-link-hover);
  cursor: pointer;
}

pre,
code,
kbd,
samp {
  font-size: 1em;
}

pre {
  display: block;
  margin-top: 0;
  margin-bottom: var(--bsi-paragraph-spacing);
  overflow: auto;
}
pre code {
  word-break: normal;
}

a > code {
  color: inherit;
}

figure {
  margin: 0 0 1rem;
}

img,
svg {
  vertical-align: middle;
}

table {
  caption-side: bottom;
  border-collapse: collapse;
}

caption {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  color: hsl(210, 17%, 44%);
  text-align: left;
}

th {
  text-align: inherit;
  text-align: -webkit-match-parent;
}

thead,
tbody,
tfoot,
tr,
td,
th {
  border-color: inherit;
  border-style: solid;
  border-width: 0;
}

label {
  display: inline-block;
}

button {
  border-radius: 0;
}

button:focus:not(:focus-visible) {
  outline: 0;
}

input,
button,
select,
optgroup,
textarea {
  margin: 0;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
}

button,
select {
  text-transform: none;
}

[role=button] {
  cursor: pointer;
}

select {
  word-wrap: normal;
}
select:disabled {
  opacity: 1;
}

[list]:not([type=date]):not([type=datetime-local]):not([type=month]):not([type=week]):not([type=time])::-webkit-calendar-picker-indicator {
  display: none !important;
}

button,
[type=button],
[type=reset],
[type=submit] {
  -webkit-appearance: button;
}
button:not(:disabled),
[type=button]:not(:disabled),
[type=reset]:not(:disabled),
[type=submit]:not(:disabled) {
  cursor: pointer;
}

::-moz-focus-inner {
  padding: 0;
  border-style: none;
}

textarea {
  resize: vertical;
}

fieldset {
  min-width: 0;
  padding: 0;
  margin: 0;
  border: 0;
}

::-webkit-datetime-edit-fields-wrapper,
::-webkit-datetime-edit-text,
::-webkit-datetime-edit-minute,
::-webkit-datetime-edit-hour-field,
::-webkit-datetime-edit-day-field,
::-webkit-datetime-edit-month-field,
::-webkit-datetime-edit-year-field {
  padding: 0;
}

::-webkit-inner-spin-button {
  height: auto;
}

[type=search] {
  outline-offset: -2px;
  -webkit-appearance: textfield;
}

/* rtl:raw:
[type="tel"],
[type="url"],
[type="email"],
[type="number"] {
  direction: ltr;
}
*/
::-webkit-search-decoration {
  -webkit-appearance: none;
}

::-webkit-color-swatch-wrapper {
  padding: 0;
}

::file-selector-button {
  font: inherit;
  -webkit-appearance: button;
}

output {
  display: inline-block;
}

iframe {
  border: 0;
}

summary {
  display: list-item;
  cursor: pointer;
}

progress {
  vertical-align: baseline;
}

[hidden] {
  display: none !important;
}

.visually-hidden,
.visually-hidden-focusable:not(:focus):not(:focus-within) {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

.upload,
.upload-avatar {
  position: absolute;
  z-index: -1;
  width: 0.1px;
  height: 0.1px;
  overflow: hidden;
  opacity: 0;
}

.upload[type=file] + label {
  display: inline-block;
  margin-bottom: 0;
  max-width: 80%;
  padding: var(--bsi-spacing-xs) var(--bsi-spacing-m);
  overflow: hidden;
  border-radius: var(--bsi-form-control-border-radius);
  background-color: var(--bsi-color-background-primary);
  color: var(--bsi-color-text-inverse);
  font-size: 1rem;
  font-weight: 700;
  transition: background-color var(--bsi-transition-instant);
  cursor: pointer;
}
.upload[type=file] + label:hover {
  background-color: var(--bsi-color-background-primary-hover);
}
.upload[type=file] + label svg {
  margin-right: var(--bsi-spacing-xxs);
  fill: var(--bsi-icon-inverse);
}

.upload-pictures-wall {
  display: flex;
  flex-wrap: wrap;
  padding: 0;
}
.upload-pictures-wall > li {
  margin-right: var(--bsi-spacing-xxs);
  margin-bottom: var(--bsi-spacing-xxs);
  list-style-type: none;
}
.upload-pictures-wall > li:last-child {
  margin-right: 0;
}
.upload-pictures-wall .upload[type=file] + label {
  display: table-cell;
  width: 128px;
  height: 128px;
  padding: 0;
  border: 2px dashed var(--bsi-color-border-primary);
  border-radius: 0;
  background-color: var(--bsi-color-border-primary-lighter);
  color: var(--bsi-color-text-primary);
  font-weight: 600;
  text-align: center;
  vertical-align: middle;
  transition: border-color var(--bsi-transition-instant);
}
.upload-pictures-wall .upload[type=file] + label .icon {
  display: block;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  margin: 0 auto;
  fill: var(--bsi-icon-primary);
}
.upload-pictures-wall .upload[type=file] + label:hover {
  border: 2px dashed var(--bsi-color-border-primary-hover);
}
.upload-pictures-wall .upload-image {
  flex-shrink: 0;
  width: 128px;
  height: 128px;
}
.upload-pictures-wall .upload-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.upload-file-list {
  margin-top: 5px;
  padding: 0;
}
.upload-file-list.upload-file-list-image .progress {
  left: 52px;
}
.upload-file-list .upload-file {
  position: relative;
  display: flex;
  align-items: center;
  max-width: 375px;
  margin-bottom: var(--bsi-spacing-s);
  list-style-type: none;
}
.upload-file-list .upload-file.uploading .icon {
  fill: var(--bsi-icon-secondary);
}
.upload-file-list .upload-file.uploading p {
  color: var(--bsi-color-text-secondary);
}
.upload-file-list .upload-file.success svg {
  fill: var(--bsi-icon-success);
}
.upload-file-list .upload-file.success p {
  color: var(--bsi-color-text-success);
}
.upload-file-list .upload-file.success p .upload-file-weight {
  display: inline-block;
}
.upload-file-list .upload-file.error svg {
  fill: var(--bsi-icon-danger);
}
.upload-file-list .upload-file.error p {
  color: var(--bsi-color-text-danger);
}
.upload-file-list .upload-file svg {
  flex-shrink: 0;
}
.upload-file-list .upload-file .upload-image {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
}
.upload-file-list .upload-file .upload-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.upload-file-list .upload-file p {
  margin: 0;
  margin-left: 12px;
  overflow: hidden;
  font-size: 0.875rem;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.upload-file-list .upload-file p .upload-file-weight {
  display: none;
  margin-left: 10px;
  color: var(--bsi-color-text-secondary);
}
.upload-file-list .upload-file button {
  margin-left: auto;
  padding: 0;
  border: 0;
  background: transparent;
}
.upload-file-list .upload-file button:hover {
  cursor: pointer;
}
.upload-file-list .upload-file button:hover svg {
  fill: var(--bsi-icon-primary);
}
.upload-file-list .progress {
  position: absolute;
  bottom: -4px;
  left: 36px;
  width: calc(100% - 45px);
  height: 4px;
  background-color: hsl(210, 3%, 85%);
  box-shadow: none;
}
.upload-file-list .progress.progress-image {
  width: calc(100% - 60px);
}

.upload-avatar-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.upload-avatar-container .upload-avatar[type=file] + label {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.upload-avatar-container .upload-avatar[type=file] + label svg {
  display: none;
}
.upload-avatar-container .upload-avatar[type=file] + label span {
  display: none;
}

.avatar-upload {
  overflow: hidden;
}

.avatar-upload-wrapper .avatar-upload-icon {
  position: absolute;
  right: 4px;
  bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 2px solid var(--bsi-color-border-inverse);
  border-radius: 50%;
  background: hsl(210, 6.2290689886%, 92.535%);
  pointer-events: none;
}
.avatar-upload-wrapper .avatar-upload-icon > svg {
  width: 16px;
  height: 16px;
}
.avatar-upload-wrapper.size-sm .avatar {
  width: 80px;
  height: 80px;
}
.avatar-upload-wrapper.size-sm .avatar-upload-icon {
  right: 4px;
  bottom: 6px;
  width: 18px;
  height: 18px;
}
.avatar-upload-wrapper.size-sm .avatar-upload-icon > svg {
  width: 12px;
  height: 12px;
}

.upload-dragdrop {
  display: inline-flex;
  align-items: center;
}
.upload-dragdrop.dragover .upload-dragdrop-image img, .upload-dragdrop.loading .upload-dragdrop-image img {
  transform: translateX(-50%) translateY(-50%) scale(0.85);
}
.upload-dragdrop.loading p:not(.upload-dragdrop-weight) {
  font-style: italic;
}
.upload-dragdrop.loading .upload-dragdrop-loading {
  opacity: 1;
}
.upload-dragdrop.loading .upload-dragdrop-text .upload-dragdrop-weight, .upload-dragdrop.success .upload-dragdrop-text .upload-dragdrop-weight {
  visibility: visible;
}
.upload-dragdrop.success .upload-dragdrop-success {
  opacity: 1;
  transform: scale(1);
}

.upload-dragdrop-text p {
  margin: 0;
  color: var(--bsi-color-text-secondary);
  font-size: 0.875rem;
}
.upload-dragdrop-text p.upload-dragdrop-weight {
  visibility: hidden;
  color: var(--bsi-color-text-secondary);
  font-size: 0.75rem;
}
.upload-dragdrop-text p.upload-dragdrop-weight svg {
  fill: var(--bsi-icon-secondary);
}
.upload-dragdrop-text h5 {
  margin: 0;
  color: var(--bsi-color-text-base);
}

.upload-dragdrop-input {
  position: absolute;
  z-index: -1;
  width: 0.1px;
  height: 0.1px;
  overflow: hidden;
  opacity: 0;
}
.upload-dragdrop-input[type=file] + label {
  color: var(--bsi-color-text-primary);
  text-decoration: underline;
  cursor: pointer;
}
.upload-dragdrop-input:focus[type=file] + label {
  outline: auto 5px -webkit-focus-ring-color;
}

.upload-dragdrop-loading {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 128px;
  height: 128px;
  overflow: hidden;
  border-radius: 50%;
  opacity: 0;
  transition: opacity 0.2s;
}
.upload-dragdrop-loading .progress-donut {
  position: relative;
}
.upload-dragdrop-loading .progress-donut .progressbar-text {
  color: #000 !important;
  font-weight: 700;
}
.upload-dragdrop-loading .progress-donut svg {
  position: relative;
}
.upload-dragdrop-loading .progress-donut svg path:first-child {
  display: none;
}
.upload-dragdrop-loading .progress-donut:before {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 0;
  content: "";
  width: 67px;
  height: 67px;
  border-radius: 50%;
  background: #fff;
  transform: translateX(-50%) translateY(-50%);
}

.upload-dragdrop-image {
  position: relative;
  flex-shrink: 0;
  width: 128px;
  height: 128px;
  margin-right: var(--bsi-spacing-s);
}
.upload-dragdrop-image img {
  position: absolute;
  top: 50%;
  left: 50%;
  display: block;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 50%;
  transform: translateX(-50%) translateY(-50%);
  transform-origin: center center;
  transition: transform 0.4s cubic-bezier(0.33, 1.07, 0.54, 1);
}
.upload-dragdrop-image .upload-dragdrop-success {
  position: absolute;
  top: calc(50% - 18px);
  left: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  opacity: 0;
  background: var(--bsi-color-background-success);
  transform: scale(0.6);
  transition: opacity 0.2s, transform 0.5s cubic-bezier(0.75, -0.5, 0, 1.75);
}
.upload-dragdrop-image .upload-dragdrop-success svg {
  width: 30px;
  height: 30px;
  fill: var(--bsi-icon-inverse);
}
.upload-dragdrop-image .upload-dragdrop-success .upload-dragdrop-text h5 + p {
  color: var(--bsi-color-text-success);
  font-weight: var(--bsi-font-weight-solid);
}

@media (min-width: 576px) {
  .upload[type=file] + label {
    font-size: var(--bsi-label-font-size-s);
  }
}
@media (min-width: 768px) {
  .upload-dragdrop-image {
    width: 180px;
    height: 180px;
    margin-right: 32px;
  }
  .upload-dragdrop-image .upload-dragdrop-success {
    left: 28px;
  }
  .upload-dragdrop-loading {
    width: 180px;
    height: 180px;
  }
  .upload-dragdrop-loading .progress-donut:before {
    content: "";
    width: 94px;
    height: 94px;
  }
}
@media (min-width: 1200px) {
  .avatar-upload:hover .upload-avatar-container,
  .avatar-upload:focus-within .upload-avatar-container {
    opacity: 1;
  }
  .upload-avatar-container {
    top: auto;
    right: 0;
    bottom: -10%;
    left: 0;
    width: 100%;
    height: 60%;
    opacity: 0;
    background-color: var(--bsi-overlay-background-primary-muted);
    transition: opacity var(--bsi-transition-instant);
  }
  .upload-avatar-container .upload-avatar[type=file] + label {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    height: 100%;
    margin: 0;
    padding-top: var(--bsi-spacing-xxs);
    color: var(--bsi-color-text-inverse);
    font-size: 0.75rem;
    text-align: center;
    cursor: pointer;
  }
  .upload-avatar-container .upload-avatar[type=file] + label span {
    display: block;
  }
  .upload-avatar-container .upload-avatar[type=file] + label svg {
    display: block;
    width: 20px;
    height: 20px;
    fill: var(--bsi-icon-inverse);
  }
  .upload-avatar-container .upload-avatar:focus:not([data-focus-mouse=true])[type=file] + label {
    outline: auto 5px -webkit-focus-ring-color;
  }
  .avatar-upload-wrapper .avatar-upload-icon {
    display: none;
  }
  .avatar-upload-wrapper.size-sm .upload-avatar[type=file] + label span {
    display: none;
  }
}
.form-feedback {
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.75rem;
}
.form-feedback.just-validate-error-label {
  color: var(--bsi-color-text-danger);
}

.input-group-text:has(~ [data-focus-mouse=true]:not(.btn)),
[data-focus-mouse=true]:not(.btn) ~ .input-group-text,
button:has(~ [data-focus-mouse=true]:not(.btn)),
[data-focus-mouse=true]:not(.btn) + button {
  border-color: inherit !important;
  box-shadow: none !important;
  outline: none !important;
}

.input-group-text:has(~ .is-invalid),
.is-invalid ~ .input-group-text,
button:has(~ .is-invalid),
.is-invalid + button {
  border-color: var(--bsi-color-border-danger) !important;
}

.sr-only-justvalidate-bi {
  display: none;
}

.just-validate-success-field {
  border-color: var(--bsi-color-border-success) !important;
  padding-right: calc(1.5em + 0.75rem) !important;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23008055' viewBox='0 0 192 512'%3E%3Cpath d='M435.848 83.466L172.804 346.51l-96.652-96.652c-4.686-4.686-12.284-4.686-16.971 0l-28.284 28.284c-4.686 4.686-4.686 12.284 0 16.971l133.421 133.421c4.686 4.686 12.284 4.686 16.971 0l299.813-299.813c4.686-4.686 4.686-12.284 0-16.971l-28.284-28.284c-4.686-4.686-12.284-4.686-16.97 0z'/%3E%3C/svg%3E");
}

.input-group-text:has(~ .just-validate-success-field),
.just-validate-success-field ~ .input-group-text,
button:has(~ .just-validate-success-field),
.just-validate-success-field + button {
  border-color: var(--bsi-color-border-success);
}

.just-validate-success-field + .input-group-text.align-buttons,
.is-invalid + .input-group-text.align-buttons {
  right: 30px;
}

.is-invalid + .input-group-text.align-buttons {
  bottom: 22px;
}

.autocomplete__wrapper .form-feedback.just-validate-error-label {
  position: absolute;
}

textarea.form-control {
  background-position: top 0.3em right 0.3em !important;
  background-size: 37px 30% !important;
}
textarea.is-invalid {
  border-bottom-style: solid;
  border-bottom-width: 1px;
}
textarea.just-validate-success-field {
  border-bottom-style: solid;
  border-bottom-width: 1px;
}

input[type=checkbox].is-invalid,
input[type=radio].is-invalid {
  --bsi-form-checkbox-border-color: var(--bsi-color-border-danger);
}

select.is-invalid {
  border: 1px solid var(--bsi-color-border-danger);
}
select.just-validate-success-field {
  border: 1px solid var(--bsi-color-border-success);
}

body {
  font-family: var(--bsi-font-sans);
}

p,
ul,
ol,
dl {
  font-size: var(--bsi-body-font-size);
  line-height: var(--bsi-body-leading);
}

caption,
figcaption {
  font-size: var(--bsi-caption-font-size);
  line-height: var(--bsi-caption-leading);
}

b,
strong {
  font-weight: var(--bsi-font-weight-strong);
}

mark,
code {
  padding: 0 var(--bsi-spacing-3xs);
}

mark {
  background-color: var(--bsi-highlight-background);
}

pre,
code,
kbd,
samp {
  border-radius: var(--bsi-radius-smooth);
  font-family: var(--bsi-font-mono);
}

ins,
del {
  position: relative;
  display: flex;
  align-items: center;
  padding: var(--bsi-spacing-3xs) var(--bsi-spacing-m);
  font: var(--bsi-body-font-size);
  text-decoration: none;
}

ins {
  background-color: var(--bsi-ins-background);
  text-decoration: none;
}

del {
  background-color: var(--bsi-del-background);
}

del::before,
ins::before {
  position: absolute;
  left: 0.5rem;
}

del::before {
  content: "−";
}

ins::before {
  content: "+";
}

kbd {
  margin: 0 2px;
  padding: 2px var(--bsi-spacing-3xs);
  border: var(--bsi-border-base) solid var(--bsi-color-border-subtle);
  background-color: var(--bsi-color-background-secondary-lighter);
  box-shadow: var(--bsi-elevation-low), 0 2px 0 0 hsla(255, 0%, 100%, 0.7) inset;
  color: var(--bsi-color-text-secondary);
  font-size: var(--bsi-font-size-1);
  font-weight: var(--bsi-font-weight-solid);
  white-space: nowrap;
}
kbd kbd {
  padding: 0;
  font-size: var(--bsi-code-font-size);
}

small,
.small {
  font-size: var(--bsi-caption-font-size);
}

.x-small {
  font-size: var(--bsi-font-size-1);
}

h1,
.h1 {
  font-size: var(--bsi-heading-1-font-size);
}

h2,
.h2 {
  font-size: var(--bsi-heading-2-font-size);
}

h3,
.h3 {
  font-size: var(--bsi-heading-3-font-size);
}

h4,
.h4 {
  font-size: var(--bsi-heading-4-font-size);
}

h5,
.h5 {
  font-size: var(--bsi-heading-5-font-size);
}

h6,
.h6 {
  font-size: var(--bsi-heading-6-font-size);
}

h1,
.h1,
h2,
.h2,
h3,
.h3,
h4,
.h4,
h5,
.h5,
h6,
.h6 {
  margin-top: 0;
  margin-bottom: var(--bsi-heading-spacing);
  line-height: var(--bsi-heading-leading);
}
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + h1, p + h1,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + .h1,
p + .h1,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + h2,
p + h2,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + .h2,
p + .h2,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + h3,
p + h3,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + .h3,
p + .h3,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + h4,
p + h4,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + .h4,
p + .h4,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + h5,
p + h5,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + .h5,
p + .h5,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + h6,
p + h6,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + .h6,
p + .h6 {
  padding-top: var(--bsi-spacing-s);
}

h1,
h2,
h3,
.h1,
.h2,
.h3 {
  font-weight: var(--bsi-heading-font-weight);
}

h4,
h5,
h6,
.h4,
.h5,
.h6 {
  font-weight: var(--bsi-heading-font-weight-weak);
}

h1,
.h1,
.display-1 {
  letter-spacing: -1px;
}

.font-serif {
  font-family: var(--bsi-font-serif) !important;
}

.font-sans-serif {
  font-family: var(--bsi-font-sans) !important;
}

.font-monospace {
  font-family: var(--bsi-font-mono) !important;
}

.display-1 {
  font-size: var(--bsi-display-font-size);
  font-weight: var(--bsi-heading-font-weight);
  line-height: var(--bsi-heading-leading);
}

.lead {
  font-size: var(--bsi-lead-font-size);
  font-weight: var(--bsi-lead-font-weight);
  line-height: var(--bsi-lead-leading);
}

blockquote,
.blockquote {
  margin: var(--bsi-spacing-m) 0;
  margin-left: var(--bsi-spacin-inline-xs);
  padding: var(--bsi-spacing-s);
  border-left: var(--bsi-border-thick) solid var(--bsi-border-color-secondary);
  font-size: var(--bsi-body-font-size);
  line-height: var(--bsi-body-leading);
}
blockquote > :last-child,
.blockquote > :last-child {
  margin-bottom: 0;
}
blockquote.text-end,
.blockquote.text-end {
  margin-right: var(--bsi-spacin-inline-xs);
  border-right: var(--bsi-border-thick) solid var(--bsi-border-color-secondary);
}
blockquote.text-center, blockquote.text-end, blockquote.blockquote-simple,
.blockquote.text-center,
.blockquote.text-end,
.blockquote.blockquote-simple {
  margin-left: 0;
  border-right: none;
  border-left: none;
}
blockquote.text-center, blockquote.blockquote-simple,
.blockquote.text-center,
.blockquote.blockquote-simple {
  padding: 0;
}
blockquote.blockquote-simple,
.blockquote.blockquote-simple {
  font-style: italic;
}
blockquote.blockquote-card,
.blockquote.blockquote-card {
  margin-left: 0;
  padding: var(--bsi-spacing-m);
  background-color: var(--bsi-color-background-inverse);
  box-shadow: var(--bsi-elevation-low);
}
blockquote.blockquote-card .blockquote-footer,
.blockquote.blockquote-card .blockquote-footer {
  font-size: inherit;
}
blockquote.blockquote-card .blockquote-footer:before,
.blockquote.blockquote-card .blockquote-footer:before {
  content: none;
}
blockquote.blockquote-card.dark,
.blockquote.blockquote-card.dark {
  border-left: none;
  background-color: var(--bsi-color-background-primary);
  color: var(--bsi-color-text-inverse);
}
blockquote.blockquote-card.dark .blockquote-footer,
.blockquote.blockquote-card.dark .blockquote-footer {
  color: var(--bsi-color-text-inverse);
}

.blockquote-footer {
  margin-top: 0;
  margin-bottom: var(--bsi-spacing-s);
  color: var(--bsi-color-text-secondary);
  font-size: var(--bsi-label-font-size);
}
.blockquote-footer::before {
  content: "— ";
}
.bg-dark .blockquote-footer {
  color: var(--bsi-color-text-muted);
}

.initialism {
  font-size: var(--bsi-label-font-size-s);
  text-transform: uppercase;
}

:host {
  display: inline-block;
}

.upload-dragdrop.loading .upload-dragdrop-select-wrap,
.upload-dragdrop.success .upload-dragdrop-select-wrap {
  display: none;
}

.invalid-feedback:not([hidden]) {
  display: block;
}

.upload-dragdrop-loading it-progress {
  display: block;
  width: 100%;
  height: 100%;
  --it-progress-donut-track-color: transparent;
}

:focus-visible,
.upload:focus-visible + label,
.upload-dragdrop-input:focus-visible[type=file] + label,
.avatar-upload:focus-within:focus-visible {
  border-color: #000 !important;
  box-shadow: 0 0 0 2px hsl(0, 0%, 100%), 0 0 0 5px hsl(0, 0%, 0%) !important;
  outline: 3px solid rgba(0, 0, 0, 0) !important;
  outline-offset: 3px !important;
}

.upload-dragdrop-weight {
  display: flex;
  align-items: center;
  gap: var(--bsi-spacing-xxs, 8px);
}

.upload-dragdrop-text :is(h1, h2, h3, h4, h6) {
  margin: 0;
  color: var(--bsi-color-text-base);
}`;

const DRAG_DROP_HEADING_LEVELS = ['h2', 'h3', 'h4', 'h5', 'h6'];

registerTranslation(translation);
registerTranslation(translation$1);
// Base64‑encoded copy of bootstrap-italia/dist/assets/upload-drag-drop-icon.svg
// bundled inline so the component works standalone without a public assets path.
const DRAG_DROP_ICON_SRC = 'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyB3aWR0aD0iMTgwcHgiIGhlaWdodD0iMTgwcHgiIHZpZXdCb3g9IjAgMCAxODAgMTgwIiB2ZXJzaW9uPSIxLjEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiPgogICAgPHRpdGxlPmljb248L3RpdGxlPgogICAgPGcgaWQ9IlVwbG9hZCIgc3Ryb2tlPSJub25lIiBzdHJva2Utd2lkdGg9IjEiIGZpbGw9Im5vbmUiIGZpbGwtcnVsZT0iZXZlbm9kZCI+CiAgICAgICAgPGcgaWQ9IkFydGJvYXJkIj4KICAgICAgICAgICAgPGcgaWQ9Imljb24iPgogICAgICAgICAgICAgICAgPHJlY3QgaWQ9IlJlY3RhbmdsZSIgZmlsbD0iI0QxRTdGRiIgeD0iMCIgeT0iMCIgd2lkdGg9IjE4MCIgaGVpZ2h0PSIxODAiPjwvcmVjdD4KICAgICAgICAgICAgICAgIDxnIGlkPSJHcm91cC02LUNvcHkiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDEwNy44NTUxNzUsIDEwOS43MDIyNDgpIHJvdGF0ZSgtMTApIHRyYW5zbGF0ZSgtMTA3Ljg1NTE3NSwgLTEwOS43MDIyNDgpIHRyYW5zbGF0ZSg0OC4zNTUxNzUsIDM1LjIwMjI0OCkiPgogICAgICAgICAgICAgICAgICAgIDxwYXRoIGQ9Ik05LjA5NDg1NzY1LDAuODE4Mzc2OTk5IEw4OS4wOTA5MjA2LDAuODE4Mzc2OTk5IEwxMTguNTkwOTIxLDMwLjQxODM3NyBMMTE4LjU5MDkyMSwxNDAuMzE0NDQgQzExOC41OTA5MjEsMTQ1LjAxMTAzNSAxMTQuNzgzNTc4LDE0OC44MTgzNzcgMTEwLjA4Njk4NCwxNDguODE4Mzc3IEw2LjI2MDIxMTk4LDE0OC44MTgzNzcgQzMuMTI5MTQ4ODMsMTQ4LjgxODM3NyAwLjU5MDkyMDY0MywxNDYuMjgwMTQ5IDAuNTkwOTIwNjQzLDE0My4xNDkwODYgTDAuNTkwOTIwNjQzLDkuMzIyMzE0MDEgQzAuNTkwOTIwNjQzLDQuNjI1NzE5MjggNC4zOTgyNjI5MywwLjgxODM3Njk5OSA5LjA5NDg1NzY1LDAuODE4Mzc2OTk5IFoiIGZpbGw9IiM2NkE1RTMiIG9wYWNpdHk9IjAuNCI+PC9wYXRoPgogICAgICAgICAgICAgICAgICAgIDxwb2x5Z29uIGZpbGw9IiNGRkZGRkYiIGZpbGwtcnVsZT0ibm9uemVybyIgcG9pbnRzPSIyMS4wNTczOTU5IDczLjg1ODQ5NTkgMjEuMDU3Mzk1OSA2NS4zNTQ1NTg5IDcwLjg2MzA0MTIgNjUuMzU0NTU4OSA3MC44NjMwNDEyIDczLjg1ODQ5NTkiPjwvcG9seWdvbj4KICAgICAgICAgICAgICAgICAgICA8cG9seWdvbiBmaWxsPSIjRkZGRkZGIiBmaWxsLXJ1bGU9Im5vbnplcm8iIHBvaW50cz0iMjAuMjE3MTkzNCA5MS4zMjIxNTkgMjAuMjE3MTkzNCA4Mi44MTgyMjIgOTkuNzIxMTMwNCA4Mi44MTgyMjIgOTkuNzIxMTMwNCA5MS4zMjIxNTkiPjwvcG9seWdvbj4KICAgICAgICAgICAgICAgICAgICA8cG9seWdvbiBmaWxsPSIjRkZGRkZGIiBmaWxsLXJ1bGU9Im5vbnplcm8iIHBvaW50cz0iMjAuMzkzMjQ1OCAxMDcuNjAwMDI4IDIwLjM5MzI0NTggOTkuMDk2MDkwNiA5OS44OTcxODI4IDk5LjA5NjA5MDYgOTkuODk3MTgyOCAxMDcuNjAwMDI4Ij48L3BvbHlnb24+CiAgICAgICAgICAgICAgICAgICAgPHBvbHlnb24gZmlsbD0iIzAwNTlCMyIgb3BhY2l0eT0iMC4yIiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgxMDUuNDQ5MzYxLCA0MC42MTA1MzUpIHNjYWxlKC0xLCAxKSByb3RhdGUoOTApIHRyYW5zbGF0ZSgtMTA1LjQ0OTM2MSwgLTQwLjYxMDUzNSkiIHBvaW50cz0iOTIuMzA0MDQyMiAyNy40NzUwNDA5IDExOC41OTQ2ODEgNTMuNzQ2MDI5NiA5NS4yNDY0MDUyIDUzLjczNzc1ODkiPjwvcG9seWdvbj4KICAgICAgICAgICAgICAgICAgICA8cGF0aCBkPSJNODkuMDkyNzUyOCwwLjg0MjY2NjY0NyBMMTE4LjU2MDYyNSwzMC4zOTE1OTQ3IEwxMDAuMjQ3NzMzLDMwLjY3MTkxNDYgQzk0LjE4MDU4OTQsMzAuNzY0Nzg1OSA4OS4xODY5MTEsMjUuOTIxNjgxNSA4OS4wOTQwMzk3LDE5Ljg1NDUzODIgQzg5LjA5MzE4MTgsMTkuNzk4NDg5NSA4OS4wOTI3NTI4LDE5Ljc0MjQzNTEgODkuMDkyNzUyOCwxOS42ODYzNzk5IEw4OS4wOTI3NTI4LDAuODQyNjY2NjQ3IFoiIGZpbGw9IiNGRkZGRkYiPjwvcGF0aD4KICAgICAgICAgICAgICAgIDwvZz4KICAgICAgICAgICAgPC9nPgogICAgICAgIDwvZz4KICAgIDwvZz4KPC9zdmc+';
let ItUploadDragDrop = class ItUploadDragDrop extends FormControl {
    constructor() {
        super(...arguments);
        // Override the base FormControlController: value is the selected File.
        this.formControlController = new FormControlController(this, {
            value: (el) => el._currentFile,
            assumeInteractionOn: ['it-change'],
        });
        /** Heading level for the title element. Defaults to 'h5'; visual size stays at h5. */
        this.headingLevel = 'h5';
        /** The currently selected File (null until file is chosen/dropped). */
        this._currentFile = null;
        this._state = 'idle';
        this._progress = 0;
        this._fileName = '';
        this._fileWeight = '';
        this._fileType = '';
        this._indeterminate = false;
        this._autoStarted = false;
    }
    getHeadingLevel() {
        return DRAG_DROP_HEADING_LEVELS.includes(this.headingLevel) ? this.headingLevel : 'h5';
    }
    get _formClass() {
        return this.composeClass('upload-dragdrop', this._state === 'dragover' ? 'dragover' : '', this._state === 'loading' ? 'loading' : '', this._state === 'success' ? 'success' : '');
    }
    /** Advance the donut progress. Value must be between 0 and 1. */
    progress(value) {
        this._indeterminate = false;
        const clamped = Math.min(1, Math.max(0, value));
        this._progress = Math.round(clamped * 100);
    }
    /** Transition to the loading state, starting the progress animation. */
    start() {
        this._state = 'loading';
    }
    /** Mark upload as successful. */
    success() {
        this._state = 'success';
    }
    /** Reset to idle state with progress at 0. */
    reset() {
        this._state = 'idle';
        this._progress = 0;
        this._indeterminate = false;
        this._autoStarted = false;
        this._fileName = '';
        this._fileWeight = '';
        this._fileType = '';
        this._currentFile = null;
    }
    _extractFileType(fileName) {
        const ext = fileName.split('.').pop();
        return ext ? ext.toUpperCase() : '';
    }
    _preventEvent(e) {
        e.preventDefault();
        e.stopPropagation();
    }
    _onDragEnter(e) {
        this._preventEvent(e);
        if (this.disabled || this._state === 'success' || this._state === 'loading')
            return;
        const event = dispatchCancelable(this, 'it-dd-start', {}, () => {
            this.start();
            this._autoStarted = true;
        });
        if (event.defaultPrevented) {
            this._state = 'dragover';
        }
    }
    _onDragOver(e) {
        e.preventDefault();
        if (this.disabled || this._state === 'success')
            return;
        if (this._state === 'idle')
            this._state = 'dragover';
    }
    _onDragLeave(e) {
        this._preventEvent(e);
        if (this.disabled)
            return;
        if (this._state === 'dragover') {
            this._state = 'idle';
        }
        else if (this._state === 'loading' && this._autoStarted && !this._currentFile) {
            this._state = 'idle';
            this._autoStarted = false;
        }
    }
    _onDragEnd(e) {
        this._preventEvent(e);
        if (this.disabled)
            return;
        if (this._state === 'dragover') {
            this._state = 'idle';
        }
    }
    _onDrop(e) {
        this._preventEvent(e);
        if (this.disabled || this._state === 'success')
            return;
        const file = e.dataTransfer?.files?.[0];
        if (!file)
            return;
        this._currentFile = file;
        this._fileName = file.name;
        this._fileWeight = formatSize(file.size);
        this._fileType = this._extractFileType(file.name);
        this._autoStarted = false;
        dispatchCancelable(this, 'it-dd-drop', { file, name: this.name, id: this.id }, () => {
            if (this._state !== 'loading')
                this.start();
            this._indeterminate = true;
        });
        this.dispatchEvent(new CustomEvent('it-change', {
            detail: { file, name: this.name, id: this.id },
            bubbles: true,
            composed: true,
        }));
    }
    _onFileInputChange(e) {
        const input = e.target;
        const file = input.files?.[0];
        if (!file)
            return;
        this._currentFile = file;
        this._fileName = file.name;
        this._fileWeight = formatSize(file.size);
        this._fileType = this._extractFileType(file.name);
        input.value = '';
        dispatchCancelable(this, 'it-dd-drop', { file, name: this.name, id: this.id }, () => {
            this.start();
            this._indeterminate = true;
        });
        this.dispatchEvent(new CustomEvent('it-change', {
            detail: { file, name: this.name, id: this.id },
            bubbles: true,
            composed: true,
        }));
    }
    _getStatusText() {
        switch (this._state) {
            case 'dragover':
                return this.$t('upload_dd_status_dragover');
            case 'loading':
                return this.$t('upload_dd_status_loading');
            case 'success':
                return this.$t('upload_dd_status_success');
            default:
                return '';
        }
    }
    _renderTextContent() {
        const hasFile = this._fileName.length > 0;
        // Title: slot or default per state
        const titleText = hasFile ? this._fileName : this.$t('upload_dd_title');
        const fileInputLabel = this.required ? this.$t('upload_label') : this.$t('upload_label');
        const canSelect = this._state === 'idle' || this._state === 'dragover';
        const isInvalid = this.formControlController.submittedOnce && this.validationMessage.length > 0;
        const feedbackId = `invalid-feedback-${this._id}`;
        const headingTag = unsafeStatic(this.getHeadingLevel());
        return html `
      <div class="upload-dragdrop-text">
        ${hasFile && this._fileWeight
            ? html `
              <p class="upload-dragdrop-weight">
                <it-icon name="it-file" size="xs" aria-hidden="true" color="secondary"></it-icon>
                <span>${this._fileType ? `${this._fileType} ` : ''}(${this._fileWeight})</span>
              </p>
            `
            : nothing}
        ${html$1 `<${headingTag} class="h5">
          <slot name="title">${titleText}</slot>
        </${headingTag}>`} ${canSelect
            ? html `
              <p>
                <slot name="description">${this.$t('upload_dd_description')}</slot>
              </p>
            `
            : nothing}
        <!-- The file input is the form-control validity participant, so it must exist in every
             state for the FormControl base class to read its validity. It is the real focusable
             control native required/validity now anchors to (no hidden proxy). The select affordance
             (label) and this wrapper are hidden via CSS in loading/success states. -->
        <p class="upload-dragdrop-select-wrap">
          <input
            type="file"
            class="upload-dragdrop-input it-form__control"
            id="${this._id}"
            accept="${ifDefined(this.accept)}"
            ?disabled="${this.disabled}"
            ?required="${this.required && !this._currentFile}"
            aria-label="${fileInputLabel}"
            aria-required="${this.required ? 'true' : nothing}"
            aria-invalid="${isInvalid ? 'true' : nothing}"
            aria-describedby="${ifDefined(isInvalid ? feedbackId : undefined)}"
            @change="${this._onFileInputChange}"
          />
          ${canSelect ? html `<label for="${this._id}">${this.$t('upload_dd_select')}</label>` : nothing}
        </p>
        ${this._state === 'loading' ? html `<p>${this.$t('upload_dd_status_loading')}</p>` : nothing}
        ${this._state === 'success' ? html `<p>${this.$t('upload_dd_status_success')}</p>` : nothing}
      </div>
    `;
    }
    render() {
        const statusText = this._getStatusText();
        const isInvalid = this.formControlController.submittedOnce && this.validationMessage.length > 0;
        const feedbackId = `invalid-feedback-${this._id}`;
        return html `
      <div
        part="drop-zone"
        class="${this._formClass}"
        @drag="${this._preventEvent}"
        @dragstart="${this._preventEvent}"
        @dragend="${this._onDragEnd}"
        @dragover="${this._onDragOver}"
        @dragenter="${this._onDragEnter}"
        @dragleave="${this._onDragLeave}"
        @drop="${this._onDrop}"
      >
        <div class="upload-dragdrop-image">
          <img src="${this.illustration ?? DRAG_DROP_ICON_SRC}" alt="" aria-hidden="true" width="180" height="180" />
          <div class="upload-dragdrop-loading" aria-hidden="${this._state !== 'loading' ? 'true' : nothing}">
            <it-progress
              type="donut"
              .value="${this._indeterminate ? 0 : this._progress}"
              it-aria-label="${this.$t('upload_dd_progress_label')}"
              ?show-value="${!this._indeterminate}"
            ></it-progress>
          </div>
          <div class="upload-dragdrop-success" aria-hidden="${this._state !== 'success' ? 'true' : nothing}">
            <it-icon name="it-check" aria-hidden="true" color="inverse"></it-icon>
          </div>
        </div>

        ${this._renderTextContent()}

        <!-- Accessible live region for state announcements -->
        <div role="status" aria-live="polite" aria-atomic="true" class="visually-hidden">${statusText}</div>
      </div>

      <div
        id="${feedbackId}"
        class="invalid-feedback form-feedback form-text just-validate-error-label"
        role="alert"
        ?hidden=${!isInvalid}
      >
        ${isInvalid ? this.validationMessage : nothing}
      </div>
    `;
    }
};
ItUploadDragDrop.styles = styles;
__decorate([
    query('.it-form__control'),
    __metadata("design:type", HTMLInputElement)
], ItUploadDragDrop.prototype, "inputElement", void 0);
__decorate([
    property({ type: String }),
    __metadata("design:type", String)
], ItUploadDragDrop.prototype, "accept", void 0);
__decorate([
    property({ type: String }),
    __metadata("design:type", String)
], ItUploadDragDrop.prototype, "illustration", void 0);
__decorate([
    property({ type: String, attribute: 'heading-level' }),
    __metadata("design:type", String)
], ItUploadDragDrop.prototype, "headingLevel", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], ItUploadDragDrop.prototype, "_currentFile", void 0);
__decorate([
    state(),
    __metadata("design:type", String)
], ItUploadDragDrop.prototype, "_state", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], ItUploadDragDrop.prototype, "_progress", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], ItUploadDragDrop.prototype, "_fileName", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], ItUploadDragDrop.prototype, "_fileWeight", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], ItUploadDragDrop.prototype, "_fileType", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], ItUploadDragDrop.prototype, "_indeterminate", void 0);
ItUploadDragDrop = __decorate([
    customElement('it-upload-drag-drop')
], ItUploadDragDrop);

export { ItUploadDragDrop };
//# sourceMappingURL=it-upload-drag-drop.js.map
